import React from 'react';
import { LinkItem } from '@model/iceberg';
import styled from '@emotion/styled';
import { TestId } from '@components/test-ids';
import { ThemeProps } from '@theme/base';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import Link from 'next/link';
import { Icon } from '../generic-icon';
import { Icons } from '@model/common/icons';
import { getImageUrl } from '@util/images';
import { breakpoints } from '@styles/breakpoints';
import { LazyLoadImage } from '../lazy-load';
import { PlainLinkStyles } from '@styles/common';

/* ***************** *
 *       Types       *
 * ***************** */

export interface LinkListProps {
  testId?: string;
  continent: ContinentProps;
  imageUrl: string;
  ctaLabel?: string;
  ctaUrl?: LinkItem;
  links: Array<LinkRow>;
}

export interface ContinentProps {
  title: string;
  link: string;
}

export interface LinkRow {
  label: string;
  subLabel: string | JSX.Element | null;
  linkUrl: LinkItem;
}

/* ****************** *
 *       Styles       *
 * ****************** */

const Container: any = styled.div({});

const ImageContainer: any = styled(LazyLoadImage)({});

const TitleRow: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.custom.spacing.large,
  justifyContent: 'space-between',
  zIndex: 1,
  ['h5']: {
    margin: 0
  },
  ['a']: {
    textDecoration: 'none'
  }
}));

const LinkRow: any = styled(TitleRow)(({ theme }: ThemeProps) => ({
  borderTop: `1px solid ${theme.custom.colors.group10.light}`,
  color: theme.custom.colors.group4.base,
  transition: `color ${theme.custom.transitions.default}`,

  ['&:hover, &:hover i']: {
    color: theme.custom.colors.group1.base
  },

  ['i']: {
    color: theme.custom.colors.group4.base
  }
}));

const LinkLabel: any = styled.p({
  flex: 1
});

const LinkRowInnerContainer: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flex: 1,
  justifyContent: 'space-between',
  alignItems: 'center',
  marginRight: theme.custom.spacing.medium
}));

export const LinkList = ({ testId, imageUrl, ctaLabel, ctaUrl, links, continent }: LinkListProps) => {
  /* *** RENDERERS *** */
  const renderLinkRows = () =>
    links.map((link: LinkRow) => (
      <Link prefetch={false} {...link.linkUrl} key={link.label} passHref style={PlainLinkStyles}>
        <LinkRow>
          <LinkRowInnerContainer>
            <LinkLabel>{link.label}</LinkLabel>
            {link.subLabel && (typeof link.subLabel === 'string' ? <p>{link.subLabel}</p> : link.subLabel)}
          </LinkRowInnerContainer>
          <Icon name={Icons.CHEVRON_RIGHT} />
        </LinkRow>
      </Link>
    ));

  return (
    <Container data-testid={testId || TestId.linkListComponent.main}>
      <ImageContainer src={getImageUrl(imageUrl, breakpoints.large)} width={400} height={225} />
      <TitleRow>
        <Link href={continent.link}>
          <h5>{continent.title}</h5>
        </Link>
        {ctaLabel && ctaUrl && (
          <Link prefetch={false} {...ctaUrl}>
            <Button
              variant={ButtonVariant.OUTLINED}
              label={ctaLabel}
              size={ButtonSize.SMALL}
              color={ButtonColor.SECONDARY}
            />
          </Link>
        )}
      </TitleRow>
      {renderLinkRows()}
    </Container>
  );
};
